import axiosClient from '../api/axiosClient';

/**
 * Barber Dashboard API Service
 * Provides functions to fetch barber-specific dashboard data from the backend
 */

const SHOP_ID = 1; // Default shop ID - you can make this dynamic later

export const barberDashboardApi = {
  /**
   * Get barber dashboard summary statistics
   */
  getBarberStats: async (barberId, dateRange = 'week', customFrom = null, customTo = null) => {
    // Calculate date range
    let fromDate, toDate;
    const today = new Date();
    
    if (customFrom && customTo) {
      fromDate = customFrom;
      toDate = customTo;
    } else {
      switch (dateRange) {
        case 'today':
          fromDate = today.toISOString().split('T')[0];
          toDate = today.toISOString().split('T')[0];
          break;
        case '7d':
          const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          fromDate = weekAgo.toISOString().split('T')[0];
          toDate = today.toISOString().split('T')[0];
          break;
        case '30d':
          const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          fromDate = monthAgo.toISOString().split('T')[0];
          toDate = today.toISOString().split('T')[0];
          break;
        default:
          const defaultWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          fromDate = defaultWeekAgo.toISOString().split('T')[0];
          toDate = today.toISOString().split('T')[0];
      }
    }

    try {
      console.log('📊 Fetching barber stats for barber:', barberId, 'date range:', fromDate, 'to', toDate);
      console.log('🔍 Barber ID type:', typeof barberId, 'value:', barberId);
      
      // Use the same API call pattern as BookingsNew.jsx
      const params = new URLSearchParams({
        shop_id: '1',
        page: '1',
        pageSize: '1000'
      });

      // Add date filters
      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);
      if (barberId) {
        params.append('barber_id', barberId);
        console.log('✅ Added barber_id filter:', barberId);
      } else {
        console.log('❌ No barber_id provided - this might be the issue!');
      }
      
      console.log('📅 API call params:', params.toString());
      console.log('🌐 Full API URL:', `/admin/bookings?${params}`);
      
      const response = await axiosClient.get(`/admin/bookings?${params}`);
      console.log('📡 Raw API response:', response.data);
      
      const bookings = response.data.items || response.data || [];
      
      console.log('📋 Barber bookings data received:', bookings.length, 'bookings');
      console.log('📋 Sample booking:', bookings[0]);
      console.log('📋 All bookings for debugging:', bookings.map(b => ({
        id: b.id,
        barber_id: b.barber_id,
        barber_user_id: b.barber_user_id,
        client_name: b.client?.name,
        service_name: b.service?.name,
        status: b.status,
        price: b.price || b.service?.price,
        start_time: b.start_time,
        created_at: b.created_at
      })));
      
      // Let's also try without date filters to see all bookings
      console.log('🔍 Testing API call without date filters...');
      const paramsNoDate = new URLSearchParams({
        shop_id: '1',
        page: '1',
        pageSize: '1000'
      });
      if (barberId) paramsNoDate.append('barber_id', barberId);
      
      const responseNoDate = await axiosClient.get(`/admin/bookings?${paramsNoDate}`);
      const allBookings = responseNoDate.data.items || responseNoDate.data || [];
      console.log('📋 All barber bookings (no date filter):', allBookings.length, 'bookings');
      console.log('📋 All bookings details:', allBookings.map(b => ({
        id: b.id,
        barber_id: b.barber_id,
        barber_user_id: b.barber_user_id,
        client_name: b.client?.name,
        service_name: b.service?.name,
        status: b.status,
        start_time: b.start_time,
        created_at: b.created_at
      })));
      
      // Calculate stats from real bookings data
      // Use all bookings (without date filter) for total stats
      const totalBookings = allBookings.length;
      
      // Fetch service prices to populate missing booking prices
      console.log('🔍 Fetching service prices for barber stats calculation...');
      let servicePrices = {};
      try {
        const servicesResponse = await axiosClient.get('/admin/services');
        let services = [];
        
        // Handle different possible response structures
        if (Array.isArray(servicesResponse.data)) {
          services = servicesResponse.data;
        } else if (servicesResponse.data.data && Array.isArray(servicesResponse.data.data)) {
          services = servicesResponse.data.data;
        } else if (servicesResponse.data.services && Array.isArray(servicesResponse.data.services)) {
          services = servicesResponse.data.services;
        } else if (servicesResponse.data.items && Array.isArray(servicesResponse.data.items)) {
          services = servicesResponse.data.items;
        } else if (servicesResponse.data.results && Array.isArray(servicesResponse.data.results)) {
          services = servicesResponse.data.results;
        }
        
        console.log('📋 Services array for barber stats:', services);
        
        if (Array.isArray(services)) {
          servicePrices = services.reduce((acc, service) => {
            console.log('🔍 Processing service for barber stats:', {
              id: service.id,
              name: service.name,
              price_cents: service.price_cents,
              price: service.price
            });
            // Convert price_cents to dollars (divide by 100)
            const price = service.price || (service.price_cents ? service.price_cents / 100 : 0);
            acc[service.id] = price;
            return acc;
          }, {});
        }
        console.log('💰 Service prices loaded for barber stats:', servicePrices);
      } catch (error) {
        console.error('❌ Failed to load service prices for barber stats:', error);
      }
      
      const totalRevenue = allBookings.reduce((sum, booking) => {
        // Try multiple sources for price and service_id
        const serviceId = booking.service_id || booking.service?.id;
        let price = booking.price || 
                   booking.service_price || 
                   booking.service?.price || 
                   (booking.service?.price_cents || 0) / 100 || 
                   0;
        
        // If no price found, look it up from service prices using the correct service_id
        if (price === 0 && serviceId && servicePrices[serviceId]) {
          price = servicePrices[serviceId];
        }
        
        console.log('💰 Barber dashboard price calculation:', {
          booking_id: booking.id,
          service_name: booking.service?.name,
          service_id: booking.service_id,
          service_nested_id: booking.service?.id,
          resolved_service_id: serviceId,
          price: booking.price,
          service_price: booking.service_price,
          service_price_field: booking.service?.price,
          service_price_cents: booking.service?.price_cents,
          service_lookup_price: servicePrices[serviceId],
          calculated_price: price
        });
        return sum + (typeof price === 'number' ? price : 0);
      }, 0);
      
      console.log('💰 Total revenue calculation:', totalRevenue);
      
      // Get unique clients
      const uniqueClients = new Set(allBookings.map(b => b.client_user_id || b.client_id).filter(Boolean)).size;
      
      // Calculate last 24h bookings
      const last24hBookings = bookings.filter(b => {
        const bookingDate = new Date(b.created_at || b.start_time || b.start_utc);
        const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
        return bookingDate > yesterday;
      }).length;
      
      // Calculate completion rate from actual data
      const completedBookings = allBookings.filter(b => b.status === 'completed').length;
      const completionRate = totalBookings > 0 ? Math.round((completedBookings / totalBookings) * 100) : 0;
      
      const stats = {
        totals: {
          bookings: totalBookings,
          revenue_cents: totalRevenue * 100, // Convert to cents
          clients: uniqueClients
        },
        last24h: {
          bookings: last24hBookings
        },
        average_rating: 4.5, // TODO: Get from reviews/ratings table
        completion_rate: completionRate
      };
      
      console.log('📊 Calculated barber stats:', stats);
      console.log('🔍 Debug info:', {
        barberId,
        fromDate,
        toDate,
        totalBookings,
        totalRevenue,
        uniqueClients,
        last24hBookings,
        completedBookings,
        completionRate
      });
      return stats;
    } catch (error) {
      console.error('❌ Failed to fetch barber stats:', error);
      return {
        totals: {
          bookings: 0,
          revenue_cents: 0,
          clients: 0
        },
        last24h: {
          bookings: 0
        },
        average_rating: 0,
        completion_rate: 0
      };
    }
  },

  /**
   * Get barber's bookings over time for charts
   */
  getBarberBookingsOverTime: async (barberId, days = 7, customFrom = null, customTo = null) => {
    try {
      console.log('📈 Fetching barber bookings over time...');
      
      // Calculate date range
      let fromDate, toDate;
      const today = new Date();
      
      if (customFrom && customTo) {
        fromDate = customFrom;
        toDate = customTo;
      } else {
        const startDate = new Date(today.getTime() - days * 24 * 60 * 60 * 1000);
        fromDate = startDate.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
      }
      
      // Fetch service prices to populate missing booking prices
      console.log('🔍 Fetching service prices for barber bookings over time...');
      let servicePrices = {};
      try {
        const servicesResponse = await axiosClient.get('/admin/services');
        let services = [];
        
        // Handle different possible response structures
        if (Array.isArray(servicesResponse.data)) {
          services = servicesResponse.data;
        } else if (servicesResponse.data.data && Array.isArray(servicesResponse.data.data)) {
          services = servicesResponse.data.data;
        } else if (servicesResponse.data.services && Array.isArray(servicesResponse.data.services)) {
          services = servicesResponse.data.services;
        } else if (servicesResponse.data.items && Array.isArray(servicesResponse.data.items)) {
          services = servicesResponse.data.items;
        } else if (servicesResponse.data.results && Array.isArray(servicesResponse.data.results)) {
          services = servicesResponse.data.results;
        }
        
        if (Array.isArray(services)) {
          servicePrices = services.reduce((acc, service) => {
            // Convert price_cents to dollars (divide by 100)
            const price = service.price || (service.price_cents ? service.price_cents / 100 : 0);
            acc[service.id] = price;
            return acc;
          }, {});
        }
      } catch (error) {
        console.error('❌ Failed to load service prices for barber bookings over time:', error);
      }
      
      // Use the same API call pattern as BookingsNew.jsx
      const params = new URLSearchParams({
        shop_id: '1',
        page: '1',
        pageSize: '1000'
      });

      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);
      if (barberId) params.append('barber_id', barberId);
      
      const response = await axiosClient.get(`/admin/bookings?${params}`);
      const bookings = response.data.items || response.data || [];
      
      console.log('📈 Barber bookings for chart:', bookings.length, 'bookings');
      
      // Group bookings by date
      const dateMap = {};
      bookings.forEach(booking => {
        const date = new Date(booking.start_time || booking.start_utc).toISOString().split('T')[0];
        if (!dateMap[date]) {
          dateMap[date] = { bookings: 0, revenue: 0 };
        }
        dateMap[date].bookings += 1;
        
        // Try multiple sources for price and service_id
        const serviceId = booking.service_id || booking.service?.id;
        let price = booking.price || booking.service_price || booking.service?.price || (booking.service?.price_cents || 0) / 100 || 0;
        
        // If no price found, look it up from service prices using the correct service_id
        if (price === 0 && serviceId && servicePrices[serviceId]) {
          price = servicePrices[serviceId];
        }
        dateMap[date].revenue += typeof price === 'number' ? price : 0;
      });
      
      // Convert to array format and fill missing dates
      const result = [];
      for (let i = 0; i < days; i++) {
        const date = new Date();
        date.setDate(date.getDate() - (days - 1 - i));
        const dateKey = date.toISOString().split('T')[0];
        
        const formattedDate = date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
        
        result.push({
          date: formattedDate,
          originalDate: dateKey,
          bookings: dateMap[dateKey]?.bookings || 0,
          revenue: dateMap[dateKey]?.revenue || 0
        });
      }
      
      console.log('📈 Processed chart data:', result);
      return result;
    } catch (error) {
      console.error('❌ Failed to fetch barber bookings over time:', error);
      return [];
    }
  },

  /**
   * Get barber's revenue over time for charts
   */
  getBarberRevenueOverTime: async (barberId, days = 7, customFrom = null, customTo = null) => {
    try {
      console.log('💰 Fetching barber revenue over time...');
      
      // Calculate date range
      let fromDate, toDate;
      const today = new Date();
      
      if (customFrom && customTo) {
        fromDate = customFrom;
        toDate = customTo;
      } else {
        const startDate = new Date(today.getTime() - days * 24 * 60 * 60 * 1000);
        fromDate = startDate.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
      }
      
      const params = new URLSearchParams();
      params.append('from', fromDate);
      params.append('to', toDate);
      params.append('barber_id', barberId);
      params.append('shop_id', SHOP_ID);
      
      const response = await axiosClient.get(`/barbers/${barberId}/revenue-timeseries?${params}`);
      const data = response.data;
      const result = Array.isArray(data) ? data : (data.chartData || data.data || []);
      console.log('💰 Barber revenue over time received:', result.length, 'data points');
      return result;
    } catch (error) {
      console.log('⚠️ Barber revenue over time endpoint failed, using fallback...', error.message);
      return [];
    }
  },

  /**
   * Get barber's service mix (most popular services)
   */
  getBarberServiceMix: async (barberId) => {
    try {
      console.log('📊 Fetching barber service mix...');
      
      // Get service mix from bookings data
      const params = new URLSearchParams({
        shop_id: '1',
        page: '1',
        pageSize: '1000'
      });

      if (barberId) params.append('barber_id', barberId);
      
      const response = await axiosClient.get(`/admin/bookings?${params}`);
      const bookings = response.data.items || response.data || [];
      
      // Group by service
      const serviceMap = {};
      bookings.forEach(booking => {
        const serviceName = booking.service_name || booking.service?.name || 'Unknown Service';
        serviceMap[serviceName] = (serviceMap[serviceName] || 0) + 1;
      });
      
      // Convert to array format
      const serviceMix = Object.entries(serviceMap).map(([name, count]) => ({
        name: name.length > 12 ? name.substring(0, 12) + '...' : name,
        value: count
      }));
      
      console.log('📊 Barber service mix:', serviceMix);
      return serviceMix;
    } catch (error) {
      console.error('Failed to fetch barber service mix:', error);
      return [];
    }
  },

  /**
   * Get barber's today schedule
   */
  getBarberTodaySchedule: async (barberId) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Use the same API call pattern as BookingsNew.jsx
      const params = new URLSearchParams({
        shop_id: '1',
        page: '1',
        pageSize: '100'
      });

      params.append('from', today);
      params.append('to', today);
      if (barberId) params.append('barber_id', barberId);
      
      const response = await axiosClient.get(`/admin/bookings?${params}`);
      const bookings = response.data.items || response.data || [];
      
      console.log('📅 Today schedule bookings:', bookings.length);
      
      return bookings
        .filter(booking => booking.status !== 'cancelled' && booking.status !== 'completed')
        .sort((a, b) => new Date(a.start_time || a.start_utc) - new Date(b.start_time || b.start_utc))
        .map(booking => ({
          time: new Date(booking.start_time || booking.start_utc).toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
          }),
          client: booking.client_name || booking.client?.name || 'Unknown Client',
          service: booking.service_name || booking.service?.name || 'Service',
          status: booking.status
        }));
    } catch (error) {
      console.error('Failed to fetch barber today schedule:', error);
      return [];
    }
  },

  /**
   * Get barber's recent bookings
   */
  getBarberRecentBookings: async (barberId, limit = 20) => {
    try {
      console.log('📋 Fetching barber recent bookings...');
      
      // Fetch service prices to populate missing booking prices
      console.log('🔍 Fetching service prices for barber recent bookings...');
      let servicePrices = {};
      try {
        const servicesResponse = await axiosClient.get('/admin/services');
        let services = [];
        
        // Handle different possible response structures
        if (Array.isArray(servicesResponse.data)) {
          services = servicesResponse.data;
        } else if (servicesResponse.data.data && Array.isArray(servicesResponse.data.data)) {
          services = servicesResponse.data.data;
        } else if (servicesResponse.data.services && Array.isArray(servicesResponse.data.services)) {
          services = servicesResponse.data.services;
        } else if (servicesResponse.data.items && Array.isArray(servicesResponse.data.items)) {
          services = servicesResponse.data.items;
        } else if (servicesResponse.data.results && Array.isArray(servicesResponse.data.results)) {
          services = servicesResponse.data.results;
        }
        
        if (Array.isArray(services)) {
          servicePrices = services.reduce((acc, service) => {
            // Convert price_cents to dollars (divide by 100)
            const price = service.price || (service.price_cents ? service.price_cents / 100 : 0);
            acc[service.id] = price;
            return acc;
          }, {});
        }
        console.log('💰 Service prices loaded for barber recent bookings:', servicePrices);
      } catch (error) {
        console.error('❌ Failed to load service prices for barber recent bookings:', error);
      }
      
      // Use the same API call pattern as BookingsNew.jsx
      const params = new URLSearchParams({
        shop_id: '1',
        page: '1',
        pageSize: limit.toString()
      });

      if (barberId) params.append('barber_id', barberId);
      
      const response = await axiosClient.get(`/admin/bookings?${params}`);
      const bookings = response.data.items || response.data || [];
      
      console.log('📋 Barber recent bookings received:', bookings.length, 'bookings');
      
      const processedBookings = bookings.map(booking => {
        // Try multiple sources for price and service_id
        const serviceId = booking.service_id || booking.service?.id;
        let price = booking.price || booking.service_price || booking.service?.price || (booking.service?.price_cents || 0) / 100 || 0;
        
        // If no price found, look it up from service prices using the correct service_id
        if (price === 0 && serviceId && servicePrices[serviceId]) {
          price = servicePrices[serviceId];
        }
        
        console.log('💰 Processing barber recent booking price:', {
          booking_id: booking.id,
          service_name: booking.service?.name,
          service_id: booking.service_id,
          service_nested_id: booking.service?.id,
          resolved_service_id: serviceId,
          price: booking.price,
          service_price: booking.service_price,
          service_price_field: booking.service?.price,
          service_price_cents: booking.service?.price_cents,
          service_lookup_price: servicePrices[serviceId],
          calculated_price: price
        });
        
        return {
          id: booking.id || `BKG-${Math.random().toString(36).substr(2, 4)}`,
          client: booking.client_name || booking.client?.name || 'Unknown Client',
          service: booking.service_name || booking.service?.name || 'Service',
          price: price,
          status: booking.status || 'confirmed',
          date: new Date(booking.start_time || booking.start_utc).toLocaleDateString()
        };
      });
      
      console.log('📋 Processed barber recent bookings:', processedBookings);
      return processedBookings;
    } catch (error) {
      console.error('❌ Failed to fetch barber recent bookings:', error);
      return [];
    }
  },

  /**
   * Get barber's availability
   */
  getBarberAvailability: async (barberId) => {
    try {
      const response = await axiosClient.get(`/barbers/${barberId}/availability`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch barber availability:', error);
      return { weekly: [], exceptions: [] };
    }
  },

  /**
   * Get barber's client list
   */
  getBarberClients: async (barberId, limit = 10) => {
    try {
      // Get clients from bookings data since we don't have a dedicated endpoint
      const params = new URLSearchParams({
        shop_id: '1',
        page: '1',
        pageSize: '1000'
      });

      if (barberId) params.append('barber_id', barberId);
      
      const response = await axiosClient.get(`/admin/bookings?${params}`);
      const bookings = response.data.items || response.data || [];
      
      // Group by client to get unique clients
      const clientMap = {};
      bookings.forEach(booking => {
        const clientId = booking.client_user_id || booking.client_id;
        const clientName = booking.client_name || booking.client?.name || 'Unknown Client';
        
        if (clientId && !clientMap[clientId]) {
          clientMap[clientId] = {
            id: clientId,
            name: clientName,
            total_visits: 0,
            last_visit: null,
            status: 'Active'
          };
        }
        
        if (clientMap[clientId]) {
          clientMap[clientId].total_visits += 1;
          const bookingDate = new Date(booking.start_time || booking.start_utc);
          if (!clientMap[clientId].last_visit || bookingDate > new Date(clientMap[clientId].last_visit)) {
            clientMap[clientId].last_visit = bookingDate.toLocaleDateString();
          }
        }
      });
      
      const clients = Object.values(clientMap).slice(0, limit);
      console.log('👥 Barber clients:', clients.length);
      return clients;
    } catch (error) {
      console.error('Failed to fetch barber clients:', error);
      return [];
    }
  },

  // Fallback methods

  /**
   * Fallback: Get barber's recent bookings and process for chart data
   */
  getBarberRecentBookingsForChart: async (barberId, days = 7, customFrom = null, customTo = null) => {
    try {
      console.log('📊 Processing barber bookings for chart data...');
      
      // Calculate date range
      let fromDate, toDate;
      const today = new Date();
      
      if (customFrom && customTo) {
        fromDate = customFrom;
        toDate = customTo;
      } else {
        const startDate = new Date(today.getTime() - days * 24 * 60 * 60 * 1000);
        fromDate = startDate.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
      }
      
      // Fetch service prices to populate missing booking prices
      console.log('🔍 Fetching service prices for barber chart data...');
      let servicePrices = {};
      try {
        const servicesResponse = await axiosClient.get('/admin/services');
        let services = [];
        
        // Handle different possible response structures
        if (Array.isArray(servicesResponse.data)) {
          services = servicesResponse.data;
        } else if (servicesResponse.data.data && Array.isArray(servicesResponse.data.data)) {
          services = servicesResponse.data.data;
        } else if (servicesResponse.data.services && Array.isArray(servicesResponse.data.services)) {
          services = servicesResponse.data.services;
        } else if (servicesResponse.data.items && Array.isArray(servicesResponse.data.items)) {
          services = servicesResponse.data.items;
        } else if (servicesResponse.data.results && Array.isArray(servicesResponse.data.results)) {
          services = servicesResponse.data.results;
        }
        
        if (Array.isArray(services)) {
          servicePrices = services.reduce((acc, service) => {
            // Convert price_cents to dollars (divide by 100)
            const price = service.price || (service.price_cents ? service.price_cents / 100 : 0);
            acc[service.id] = price;
            return acc;
          }, {});
        }
      } catch (error) {
        console.error('❌ Failed to load service prices for barber chart data:', error);
      }
      
      const params = new URLSearchParams();
      params.append('from', fromDate);
      params.append('to', toDate);
      params.append('barber_id', barberId);
      params.append('shop_id', SHOP_ID);
      params.append('pageSize', 1000);
      
      const response = await axiosClient.get(`/admin/bookings?${params}`);
      const bookings = response.data.items || response.data || [];
      
      console.log('📊 Barber bookings for chart processing:', bookings.length, 'bookings');
      
      // Process bookings by day
      const dailyData = {};
      
      bookings.forEach(booking => {
        const date = new Date(booking.start_time);
        const dateKey = date.toISOString().split('T')[0];
        
        if (!dailyData[dateKey]) {
          dailyData[dateKey] = { date: dateKey, bookings: 0, revenue: 0 };
        }
        
        dailyData[dateKey].bookings++;
        // Try multiple sources for price and service_id
        const serviceId = booking.service_id || booking.service?.id;
        let price = booking.price || booking.service_price || booking.service?.price || (booking.service?.price_cents || 0) / 100 || 0;
        
        // If no price found, look it up from service prices using the correct service_id
        if (price === 0 && serviceId && servicePrices[serviceId]) {
          price = servicePrices[serviceId];
        }
        dailyData[dateKey].revenue += typeof price === 'number' ? price : 0;
      });
      
      // Convert to array and sort
      const result = Object.values(dailyData).sort((a, b) => new Date(a.date) - new Date(b.date));
      
      console.log('📊 Barber chart data processed:', result);
      return result;
    } catch (error) {
      console.error('❌ Failed to process barber bookings for chart:', error);
      return [];
    }
  },

  /**
   * Fallback: Get barber service mix from bookings
   */
  getBarberServiceMixFromBookings: async (barberId) => {
    try {
      const params = new URLSearchParams();
      params.append('barber_id', barberId);
      params.append('shop_id', SHOP_ID);
      params.append('pageSize', 1000);
      
      const response = await axiosClient.get(`/admin/bookings?${params}`);
      const bookings = response.data.items || response.data || [];
      
      // Group by service
      const serviceData = {};
      bookings.forEach(booking => {
        const serviceName = booking.service_name || booking.service?.name || 'Unknown Service';
        serviceData[serviceName] = (serviceData[serviceName] || 0) + 1;
      });
      
      // Convert to array format
      return Object.entries(serviceData).map(([name, count]) => ({
        name: name.length > 12 ? name.substring(0, 12) + '...' : name,
        value: count
      }));
    } catch (error) {
      console.error('Failed to get barber service mix from bookings:', error);
      return [
        { name: "Haircut", value: 45 },
        { name: "Beard Trim", value: 30 },
        { name: "Styling", value: 15 },
        { name: "Other", value: 10 }
      ];
    }
  }
};

export default barberDashboardApi;
